<template>
  <div class="o-costListing">
    <bos-loader v-if="isLoading" minHeight />

    <div v-else class="costListing-content">
      <p>{{ $t('components.costListing.changehint') }}</p>

      <h2>{{ $t('components.costListing.costtitle') }}</h2>

      <button class="showall plus" v-if="allminified" @click="openAll">
        <font-awesome-icon icon="plus" />
        {{ $t('components.costListing.showall') }}
      </button>

      <button class="showall minus" v-if="!allminified" @click="collapseAll">
        <font-awesome-icon icon="minus" />
        {{ $t('components.costListing.hideall') }}
      </button>

      <ul class="content-accordion">
        <li class="accordion-section" v-for="cost in costs" :key="cost.id">
          <button class="section-toggler" v-b-toggle="cost.accordion.id">
            <font-awesome-icon
              class="toggler-icon__expand"
              icon="minus"
            /><font-awesome-icon class="toggler-icon__collapse" icon="plus" />{{
              cost.accordion.title
            }}

            <div class="total">
              {{ $t('components.costListing.table.total.foot.currency') }}
              {{ getCostTableTotal(cost.table.items) }}
            </div>
          </button>

          <!-- Write <b-collapse accordion="costAccordion"> to allow only one open collapse at the moment -->
          <b-collapse class="section-tab" :id="cost.accordion.id">
            <div class="tab-inner">
              <b-table
                :items="cost.table.items"
                :fields="cost.table.fields"
                responsive
              >
              </b-table>
            </div>
          </b-collapse>
        </li>
      </ul>

      <div class="content-total">
        <!-- h4>{{ $t('components.costListing.totaltitle') }}</h4 -->

        <b-table
          :items="total.items"
          :fields="total.fields"
          borderless
          responsive
        >
          <template v-slot:head()="data">
            <span aria-hidden="true">{{ data.label }}</span>
          </template>

          <template v-slot:custom-foot="data">
            <tr>
              <th class="col-totaltext">
                {{ $t('components.costListing.table.total.foot.totaltext') }}
              </th>
              <th class="col-currency">
                {{ $t('components.costListing.table.total.foot.currency') }}
              </th>
              <th class="col-summary">{{ getTotalTableSummary() }}</th>
            </tr>
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import {
  getLocalizationInExcludes,
  localize,
  numberFormatter
} from '@/assets/js/helper'
import BosLoader from '@/components/BosLoader'
import { VBToggle, BCollapse, BTable } from 'bootstrap-vue'

Vue.directive('b-toggle', VBToggle)

export default {
  name: 'CostListing',
  props: {
    booth: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },
  data() {
    return {
      allminified: true,
      costTablesOptions: {
        fields: {
          hidden: ['id', 'accordion', 'numTotal'],
          sort: [
            'article',
            'description',
            '__unknown__',
            'amount',
            'cost',
            'total'
          ]
        }
      }
    }
  },
  computed: {
    boothId() {
      return this.booth.regiNr || (this.$route.params || {}).booth || ''
    },
    isLoading() {
      return this.$store.getters['fair/isLoadingBoothCost'](this.boothId)
    },
    boothIsNew() {
      return this.boothId === 'new'
    },
    boothCosts() {
      let costs = this.$store.getters['fair/getBoothCost'](this.boothId)
      let data = costs.data !== undefined ? costs.data : costs || []

      let addPositionFromBooth = (
        !this.booth.paketKopfLeistung && this.booth.standLeistungId !== undefined
      );

      let boothGroupName = null;
      let boothGroupId = null;
      
      let currentLang = this.$store.getters['ui/getLocale'].substring(0, 2)
      currentLang = currentLang.charAt(0).toUpperCase() + currentLang.slice(1)
      // has entries that are part of a package, the package beeing the booth itself
      const costsOfBooth = costs.data.filter(item => item.paketTeil === true && item.regiNrPaketKopf === this.booth.regiNr)
      if (costsOfBooth.length > 0) {
        addPositionFromBooth = true
        boothGroupName = costsOfBooth[0]['leistungsgruppeBezeichnung' + currentLang]
        boothGroupId = costsOfBooth[0].leistungsgruppeId
      }

      if (
        addPositionFromBooth
      ) {
        data = data.filter(
          item => item.buchungId !== 'b' + this.booth.standLeistungId
        )

        if (boothGroupId == null) {
          boothGroupId = 's' + this.booth.standLeistungId
          boothGroupName = this.booth[
            'standLeistungBezeichnung' + currentLang
          ]
        }

        data.unshift({
          leistungsgruppeId: boothGroupId,
          leistungsgruppeBezeichnung: boothGroupName,
          buchungId: 'b' + this.booth.standLeistungId,
          leistungsId: this.booth.standLeistungId,
          leistungsBezeichnung: this.booth[
            'standLeistungBezeichnung' + currentLang
          ],
          menge: 1,
          einzelPreisNetto: this.booth.gesamtpreisNetto,
          gesamtPreisNetto: this.booth.gesamtpreisNetto
        })
      }

      let inAndExcludes = getLocalizationInExcludes(this.$i18n.locale)

      return localize(data, inAndExcludes.include, inAndExcludes.exclude)
    },
    costs() {
      //console.log(this.boothCosts)
      return this.boothCosts.reduce((accordion, cost) => {
        let accordionType = cost.leistungsgruppeId || 'UNDEFINED'
        let accordionId = `accordion-${accordionType}`

        accordion[accordionType] = accordion[accordionType] || {
          accordion: {},
          table: { items: [], fields: [], foot: [] }
        }

        accordion[accordionType].accordion = {
          id: accordionId,
          title: cost.leistungsgruppeBezeichnung
        }

        let numTotal = 0;

        if (cost.paketTeil && cost.paketTeil === true) {
          numTotal = null;
        } else {
          numTotal = cost.gesamtPreisNetto;
        }

        accordion[accordionType].table.items.push({
          id: cost.buchungId,
          article: cost.leistungsId,
          description: cost.leistungsBezeichnung,
          amount:
            cost.einheit === undefined
              ? cost.menge
              : `${cost.menge} ${cost.einheit}`,
          cost:
              cost.einzelPreisNetto === undefined ? null :
              numberFormatter(cost.einzelPreisNetto),
          total:
              cost.gesamtPreisNetto === undefined ? null :
              numberFormatter(cost.gesamtPreisNetto),
          numTotal:
            numTotal
        })

        accordion[accordionType].table.fields = Object.keys(
          accordion[accordionType].table.items[0]
        )
          .filter(key => !this.costTablesOptions.fields.hidden.includes(key))
          .sort((keyA, keyB) => {
            let indexUnknown = this.costTablesOptions.fields.sort.findIndex(
              key => key === '__unknown__'
            )
            let indexA = this.costTablesOptions.fields.sort.findIndex(
              key => key === keyA
            )
            let indexB = this.costTablesOptions.fields.sort.findIndex(
              key => key === keyB
            )

            indexUnknown = indexUnknown >= 0 ? indexUnknown : Infinity
            indexA = indexA >= 0 ? indexA : indexUnknown
            indexB = indexB >= 0 ? indexB : indexUnknown

            return indexA > indexB ? 1 : indexA < indexB ? -1 : 0
          })
          .map(key => {
            let translationKey = `components.costListing.table.costs.head.${key}`

            return {
              key,
              label: this.$te(translationKey)
                ? this.$t(translationKey)
                : key.charAt(0).toUpperCase() + key.slice(1),
              class: `col-${key}`
            }
          })

        return accordion
      }, {})
    },
    total() {
      let costTableFields =
        ((this.costs[Object.keys(this.costs)[0]] || {}).table || {}).fields ||
        []

      return {
        items: [],
        fields: ['totaltext', 'currency', 'summary'].map((key, keyIndex) => {
          return {
            key,
            label:
              (costTableFields[costTableFields.length + keyIndex - 3] || {})
                .label || '',
            class: `col-${key}`
          }
        })
      }
    }
  },
  mounted() {
    this.$root.$on('bv::collapse::state', (collapseId, isJustShown) => {
      if (
        this.booth === null ||
        JSON.stringify(this.booth) === JSON.stringify({})
      ) {
        return
      }
      if (this.booth.costCollapse === undefined) {
        this.booth.costCollapse = []
      }
      if (isJustShown) {
        this.booth.costCollapse.push(collapseId)
      } else {
        this.booth.costCollapse = this.booth.costCollapse.filter(
          item => item !== collapseId
        )
      }
      if (this.booth.costCollapse.length > 0) {
        this.allminified = false
      } else {
        this.allminified = true
      }
    })
  },
  methods: {
    collapseAll() {
      this.booth.costCollapse.forEach(item => {
        this.$root.$emit('bv::toggle::collapse', item)
      })
    },
    openAll() {
      for (let key in this.costs) {
        this.$root.$emit('bv::toggle::collapse', this.costs[key].accordion.id)
      }
    },

    getCostTableTotal(tableItems = [], formatted = true) {
      let summary = tableItems.reduce(
        (sum, item) => (sum += item.numTotal || 0),
        0
      )
      return formatted ? numberFormatter(summary) : summary
    },
    getTotalTableSummary(formatted = true) {
      let summary = Object.keys(this.costs).reduce(
        (sum, costGroupKey) =>
          (sum += this.getCostTableTotal(
            this.costs[costGroupKey].table.items,
            false
          )),
        0
      )
      return formatted ? numberFormatter(summary) : summary
    }
  },
  components: {
    BosLoader,
    BCollapse,
    BTable
  }
}
</script>

<style lang="scss">
@import '@/assets/css/variables.scss';



$costlisting-accordion-section-seperator: 1px solid lighten($brand-gray, 25%) !default;

$costlisting-accordion-section-toggler-padding: 0.6em 10px !default;
$costlisting-accordion-section-toggler-font-family: inherit !default;
$costlisting-accordion-section-toggler-font-size: inherit !default;
$costlisting-accordion-section-toggler-font-weight: $font-weight-bold !default;
$costlisting-accordion-section-toggler-color: inherit !default;
$costlisting-accordion-section-toggler-icon-gap: 0.5em !default;
$costlisting-accordion-section-toggler-icon-size: 1em !default;
$costlisting-accordion-section-toggler-icon-color: $brand-blue !default;

$costlisting-accordion-section-open-toggler-font-family: $costlisting-accordion-section-toggler-font-family !default;
$costlisting-accordion-section-open-toggler-font-size: $costlisting-accordion-section-toggler-font-size !default;
$costlisting-accordion-section-open-toggler-font-weight: $costlisting-accordion-section-toggler-font-weight !default;
$costlisting-accordion-section-open-toggler-color: $brand-blue !default;
$costlisting-accordion-section-open-toggler-icon-gap: 0.5em !default;
$costlisting-accordion-section-open-toggler-icon-size: 1em !default;
$costlisting-accordion-section-open-toggler-icon-color: $brand-blue !default;

$costlisting-accordion-section-tab-padding: 0 0 4.5rem 1.5em !default;
$costlisting-accordion-section-tab-transition: all 200ms ease-out !default;

$costlisting-table-border: 1px solid lighten($brand-gray, 25%) !default;

$costlisting-total-margin: 2.25rem 0 0 !default;
$costlisting-total-border-top: 2px solid $brand-black !default;
$costlisting-total-border-bottom: 1px solid $brand-black !default;
$costlisting-total-font-weight: $font-weight-bold !default;

table {
  margin-bottom: 0 !important;
}


.o-costListing {
  h2 {
    color: $brand-primary !important;
  }

  table {
    th {
      font-size: 1rem !important;
    }
  }
  .showall {
    display: block;
    padding: 0.6em 10px;
    width: 100%;
    background: none;
    border: 0 none;
    font-family: inherit;
    font-size: 20px;
    font-weight: 300;
    color: inherit;
    text-align: left;
    line-height: 19px;
    margin-bottom: 20px;

    svg {
      margin-right: 0.5em;
      width: 1em;
      height: 1em;
      color: $brand-primary;
    }
  }

  .costListing-content {
    .content-accordion {
      margin: 0;
      padding: 0;
      list-style: none;

      .accordion-section {
        border-top: $costlisting-accordion-section-seperator;

        &:last-child {
          border-bottom: $costlisting-accordion-section-seperator;
        }

        .section-toggler {
          display: block;
          padding: $costlisting-accordion-section-toggler-padding;
          width: 100%;
          background: none;
          border: 0 none;
          font-family: $costlisting-accordion-section-toggler-font-family;
          font-size: $costlisting-accordion-section-toggler-font-size;
          font-weight: $costlisting-accordion-section-toggler-font-weight;
          color: $costlisting-accordion-section-toggler-color;
          text-align: left;
          line-height: 19px;
          position: relative;

          svg {
            margin-right: $costlisting-accordion-section-toggler-icon-gap;
            width: $costlisting-accordion-section-toggler-icon-size;
            height: $costlisting-accordion-section-toggler-icon-size;
            color: $brand-primary;
          }

          .total {
            position: absolute;
            top: 0.6em;
            right: 10px;
          }

          &:hover {
            background-color: #f7f6f3;
          }

          &[aria-expanded='false'] {
            svg {
              &.toggler-icon__expand {
                display: none;
              }
            }
          }

          &[aria-expanded='true'] {
            font-family: $costlisting-accordion-section-open-toggler-font-family;
            font-size: $costlisting-accordion-section-open-toggler-font-size;
            font-weight: $costlisting-accordion-section-open-toggler-font-weight;
            color: $brand-primary;

            svg {
              margin-right: $costlisting-accordion-section-open-toggler-icon-gap;
              width: $costlisting-accordion-section-open-toggler-icon-size;
              height: $costlisting-accordion-section-open-toggler-icon-size;
              color: $brand-primary;

              &.toggler-icon__collapse {
                display: none;
              }
            }
          }
        }

        .section-tab {
          overflow: hidden;
          transition: $costlisting-accordion-section-tab-transition;

          .tab-inner {
            padding: $costlisting-accordion-section-tab-padding;

            table {
              th,
              td {
                width: 100%;
                border-top: $costlisting-table-border;
                border-bottom: $costlisting-table-border;
                text-align: left;

                &.col-article,
                &.col-amount,
                &.col-cost,
                &.col-total {
                  width: 0%;
                  white-space: nowrap;
                }

                &.col-amount,
                &.col-cost,
                &.col-total,
                &.col-totaltext,
                &.col-currency,
                &.col-summary {
                  text-align: right;
                }
              }
            }
          }
        }
      }
    }

    .content-total {
      margin: $costlisting-total-margin;

      table {
        border-top: $costlisting-total-border-top;
        border-bottom: $costlisting-total-border-bottom;

        thead {
          > tr {
            > th,
            > td {
              padding-top: 0 !important;
              padding-bottom: 0 !important;

              > span {
                display: block;
                height: 0;
                overflow: hidden;
              }
            }
          }
        }

        th,
        td {
          width: 100%;
          text-align: left;

          &.col-currency,
          &.col-summary {
            width: 0%;
            white-space: nowrap;
          }

          &.col-totaltext,
          &.col-currency,
          &.col-summary {
            font-weight: $costlisting-total-font-weight;
            text-align: right;
          }
        }
      }
    }
  }

  @media print {
    .costListing-content {
      .content-accordion {
        .section-toggler {
          svg {
            display: none;
          }
        }

        .section-tab {
          display: block !important;
        }
      }
    }
  }
}
</style>
